// components/portal/StopButton.tsx
//
// The one stop affordance for the whole app. There were five copies of it: a red block in the
// composer, a black chip in the master pill, and three rose-tinted "Abort" pills in the campaign
// rows and the campaign chat block. Five weights of alarm for an action that is neither dangerous
// nor irreversible, which is why it read as loud and ugly.
//
// The skin is deliberately quiet: a soft grey square with rounded corners, sized and coloured like
// the composer controls it sits next to (the plus, the mic, the send arrow), holding a white
// stroked square. No label, no red, no pulse. The word lives in aria-label / title only.
//
// This is skin only. Every call site keeps its own handler and its own conditions.
import React from 'react';
import clsx from 'clsx';
import { useKernelStore } from '@/kernel/kernelStores';

/** Composer slot: same box as the send arrow it replaces (p-2 around a 14px icon). */
export const STOP_BUTTON_SIZE_COMPOSER = 30;
/** Master pill composer: same box as its h-9 w-9 send button. */
export const STOP_BUTTON_SIZE_PILL = 36;
/** Table rows and chat blocks, where the neighbouring row actions are ~24px. */
export const STOP_BUTTON_SIZE_INLINE = 24;

interface StopButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'type'> {
  /** Box side in px. Pick one of the STOP_BUTTON_SIZE_* constants so sites stay in sync. */
  size?: number;
  /** Overrides the default bilingual label used for aria-label and title. */
  label?: string;
  /** ChatGPT's composer stop (owner, 2026-09-28): the send button's blue circle holding a filled
   *  white square, in the same place and shape as the arrow it replaces. */
  round?: boolean;
}

/**
 * forwardRef because the composer wraps this in a Radix `TooltipTrigger asChild`, which needs to
 * hand its own ref and props down to the real button.
 */
const StopButton = React.forwardRef<HTMLButtonElement, StopButtonProps>(function StopButton(
  { size = STOP_BUTTON_SIZE_COMPOSER, label, round = false, className, style, ...rest },
  ref,
) {
  const lang = useKernelStore((state) => state.language);
  const accessibleLabel = label ?? (lang === 'fr' ? 'Arrêter' : 'Stop');
  // Optically centred: the glyph reads as a square at ~40% of the box, the same ratio the lucide
  // icons in the neighbouring controls end up at.
  const glyph = Math.round(size * (round ? 0.34 : 0.4));

  return (
    <button
      ref={ref}
      type="button"
      aria-label={accessibleLabel}
      title={accessibleLabel}
      className={clsx(
        'inline-flex shrink-0 items-center justify-center text-white transition-colors duration-150',
        round
          ? 'bg-[#3862C2] hover:bg-[#4270D6] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#3862C2]'
          : 'bg-[#3A3A3A] hover:bg-[#474747] active:bg-[#525252] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#3A3A3A]',
        className,
      )}
      style={{
        width: size,
        height: size,
        // Rounded, not circular, outside the composer: there it must not read as the send pill.
        borderRadius: round ? '9999px' : Math.round(size * 0.32),
        ...style,
      }}
      {...rest}
    >
      <svg
        width={glyph}
        height={glyph}
        viewBox="0 0 12 12"
        aria-hidden="true"
        focusable="false"
      >
        <rect
          x="1.35"
          y="1.35"
          width="9.3"
          height="9.3"
          rx={round ? 1.8 : 1}
          fill={round ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="1.2"
        />
      </svg>
    </button>
  );
});

export default StopButton;
